import React from 'react';

const Services: React.FC = () => {
  const services = [
    {
      id: 1,
      title: 'Workflow Automation',
      description: 'Streamline your business processes and eliminate repetitive tasks. Save time and reduce human error with intelligent automation.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      features: ['Task Scheduling', 'Process Mining', 'No-Code Builders'],
    },
    {
      id: 2,
      title: 'AI Integration',
      description: 'Leverage cutting-edge AI technology to enhance decision-making and unlock new business opportunities.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      features: ['Machine Learning', 'Natural Language Processing', 'Predictive Analytics'],
    },
    {
      id: 3,
      title: 'Data Pipelines',
      description: 'Build robust data infrastructure. Transform, analyze, and visualize your data effortlessly.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
      ),
      features: ['Real-time Processing', 'Data Warehousing', 'ETL Solutions'],
    },
    {
      id: 4,
      title: 'Custom Development',
      description: 'Tailored software solutions built specifically for your unique business requirements.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      features: ['API Development', 'Custom Integrations', 'Enterprise Solutions'],
    },
    {
      id: 5,
      title: 'Cloud Infrastructure',
      description: 'Scalable, secure, and cost-effective cloud solutions to power your business operations.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      ),
      features: ['Cloud Migration', 'DevOps & CI/CD', 'Infrastructure as Code'],
    },
    {
      id: 6,
      title: 'Consulting & Strategy',
      description: 'Expert guidance to help you navigate digital transformation and achieve your goals.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      features: ['Digital Strategy', 'Process Optimization', 'Team Training'],
    },
  ];

  return (
    <section id="services" className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-maize-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-maize-400/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="reveal inline-block px-4 py-1.5 text-sm font-medium text-maize-500 bg-maize-500/10 rounded-full mb-4">
            Our Services
          </span>
          <h2 className="reveal text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">Transform Your </span>
            <span className="gradient-text">Business</span>
          </h2>
          <p className="reveal text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Comprehensive solutions designed to streamline operations, reduce costs, 
            and accelerate your digital transformation journey.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="reveal group hover-lift"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="h-full p-8 glass-light rounded-2xl border border-white/5 hover:border-maize-500/30 transition-all duration-500 relative overflow-hidden">
                {/* Hover gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-maize-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Icon */}
                <div className="relative z-10 w-14 h-14 flex items-center justify-center bg-gradient-to-br from-maize-400/20 to-maize-500/10 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  <div className="text-maize-400">
                    {service.icon}
                  </div>
                </div>

                {/* Content */}
                <h3 className="relative z-10 text-xl font-bold text-white mb-3 group-hover:text-maize-400 transition-colors duration-300">
                  {service.title}
                </h3>
                
                <p className="relative z-10 text-gray-400 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <div className="relative z-10 flex flex-wrap gap-2">
                  {service.features.map((feature, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 text-xs font-medium text-gray-500 bg-white/5 rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Arrow indicator */}
                <div className="absolute bottom-8 right-8 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                  <svg className="w-5 h-5 text-maize-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="reveal mt-16 text-center">
          <a 
            href="#contact"
            className="inline-flex items-center gap-2 text-maize-400 hover:text-maize-300 font-medium transition-colors duration-300"
          >
            View all services
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;